import { draftScopeIcon } from "./draft-scope.ts";
import type { ChatTab, ScopeTab } from "./tabs.ts";

/**
 * The chat entry row and its scope picker as plain data (P1.9-T40, ADR-068's amendment "the entry row as redrawn, and
 * the scope picker"; the design's chat panel, a visual reference): the row's sizes, the scopes the picker lists,
 * where plain text goes once a scope is picked, what picking does to the draft, and the list's keys. Pure: the entry
 * row holds the picked scope and hands it in.
 */

/**
 * The entry row's sizes in logical px, the design's: the row's height, the picker's width, Send's side, the gap
 * between the three, the picker's caret, and how far the field's text sits from the field's outer edge (the design's 1 px edge and 3 px of padding).
 */
export const ENTRY_ROW = { height: 16, pickerWidth: 22, send: 16, gap: 1, caret: { width: 3, height: 2 }, fieldTextInset: 4 } as const;

/**
 * One part of the entry row: where it starts from the row's left, and its size.
 */
export interface EntryPart {
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Lays the entry row out across a width: the picker at the left, Send at the right, and the field filling what the
 * two and their gaps leave.
 *
 * @param width The row's width in whole logical px.
 * @returns The picker, the field and Send.
 * @example
 * entryRowLayout(190).field;
 * // { left: 23, width: 150, height: 16 }
 */
export function entryRowLayout(width: number): { readonly picker: EntryPart; readonly field: EntryPart; readonly send: EntryPart } {
  const { height, pickerWidth, send, gap } = ENTRY_ROW;
  const fieldLeft = pickerWidth + gap;
  const fieldWidth = Math.max(0, width - fieldLeft - gap - send);
  return {
    picker: { left: 0, width: pickerWidth, height },
    field: { left: fieldLeft, width: fieldWidth, height },
    send: { left: fieldLeft + fieldWidth + gap, width: send, height },
  };
}

/**
 * The scopes the picker lists: every open tab but All, in the tabs' order, so a scope the command map has not opened
 * is never listed.
 *
 * @param open The open tabs.
 * @returns The scopes.
 */
export function pickerScopes(open: readonly ChatTab[]): readonly ScopeTab[] {
  return open.filter((tab): tab is ScopeTab => tab !== "all");
}

/**
 * The tab whose scope plain text goes to: the picked scope while it is open, else the tab shown.
 *
 * @param picked The scope picked in the entry row, or null for none.
 * @param shown The tab shown.
 * @param open The open tabs.
 * @returns The tab a draft that is no command speaks under.
 */
export function speakingTab(picked: ScopeTab | null, shown: ChatTab, open: readonly ChatTab[]): ChatTab {
  return picked !== null && open.includes(picked) ? picked : shown;
}

/**
 * A scope's command at the front of a draft, with the whisper's two names: what picking another scope drops.
 */
const SCOPE_COMMAND = /^\s*\/(?:(?:w|whisper)(?:\s+\S+){0,2}|re|reply|\^|g|t|h|p|me)(?:\s+|$)/i;

/**
 * The draft after a scope is picked: a draft already in that scope, plain text and a command that names no scope are
 * left as typed; a draft whose command names another scope loses the command, a whisper's two names with it, and
 * keeps its message, which then goes to the picked scope.
 *
 * @param draft The input's text.
 * @param scope The scope picked.
 * @returns The draft to hold.
 * @example
 * pickScope("/g hello there", "local");
 * // "hello there"
 */
export function pickScope(draft: string, scope: ScopeTab): string {
  const named = draftScopeIcon(draft);
  if (named === null || named === scope || !draft.trimStart().startsWith("/")) {
    return draft;
  }
  return draft.replace(SCOPE_COMMAND, "");
}

/**
 * What a key does while the picker's list shows.
 */
export type PickerMove = { readonly kind: "move"; readonly to: ScopeTab } | { readonly kind: "choose"; readonly scope: ScopeTab } | { readonly kind: "close" };

/**
 * A key while the picker's list shows: Up and Down move the highlight, wrapping, Home and End reach the first and
 * the last, Enter and Space choose, and Tab closes the list on its way out. Those are the only keys the list takes
 * (ADR-061's amendment of 2026-10-03); Escape is the input router's.
 *
 * @param scopes The listed scopes.
 * @param highlighted The highlighted scope; one not listed counts as the first.
 * @param key The key, as `KeyboardEvent.key` names it.
 * @returns The move, or null for a key the list does not take or a list with no scope.
 */
export function pickerKey(scopes: readonly ScopeTab[], highlighted: ScopeTab, key: string): PickerMove | null {
  const at = Math.max(0, scopes.indexOf(highlighted));
  const current = scopes[at];
  if (current === undefined) {
    return null;
  }
  switch (key) {
    case "ArrowDown":
      return { kind: "move", to: scopes[(at + 1) % scopes.length] ?? current };
    case "ArrowUp":
      return { kind: "move", to: scopes[(at - 1 + scopes.length) % scopes.length] ?? current };
    case "Home":
      return { kind: "move", to: scopes[0] ?? current };
    case "End":
      return { kind: "move", to: scopes.at(-1) ?? current };
    case "Enter":
    case " ":
      return { kind: "choose", scope: current };
    case "Tab":
      return { kind: "close" };
    default:
      return null;
  }
}
